import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/lib/permissions";
import { GeoArcMap } from "@/components/GeoArcMap";
import { IntroNote } from "@/components/IntroNote";
import { toast } from "sonner";
import {
  Globe2, Ship, Boxes, Plane, Zap, ArrowRight, Crosshair, ShieldAlert, TrendingUp,
} from "lucide-react";

const SHOCKS = [
  { value: "chokepoint", label: "Chokepoint closure", icon: Ship },
  { value: "dyad", label: "Nation-pair escalation", icon: Crosshair },
  { value: "material", label: "Material shortage", icon: Boxes },
  { value: "visa", label: "Visa / labour freeze", icon: Plane },
];

const gbp = (n) => "£" + (n || 0).toLocaleString("en-GB");
const gbpShort = (n) => {
  n = n || 0;
  if (n >= 1e9) return "£" + (n / 1e9).toFixed(1) + "bn";
  if (n >= 1e6) return "£" + (n / 1e6).toFixed(1) + "m";
  return "£" + n.toLocaleString("en-GB");
};
const bandColor = (b) => (b === "red" ? "text-red-600" : b === "amber" ? "text-amber-600" : "text-emerald-600");
const idxColor = (v) => (v >= 60 ? "bg-red-500" : v >= 35 ? "bg-amber-500" : "bg-emerald-500");

function Bar({ label, value }) {
  return (
    <div>
      <div className="flex justify-between text-[11px] text-ek-ink/65"><span>{label}</span><span>{Math.round(value * 100)}%</span></div>
      <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-black/5">
        <div className={`h-full rounded-full ${idxColor(value * 100)}`} style={{ width: `${value * 100}%` }} />
      </div>
    </div>
  );
}

export default function Geopolitics() {
  const { user } = useAuth();
  const canWrite = hasPermission(user, "risk.write");
  const [summary, setSummary] = useState(null);
  const [names, setNames] = useState({});
  const [chokepoints, setChokepoints] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [options, setOptions] = useState(null);
  const [shock, setShock] = useState("chokepoint");
  const [target, setTarget] = useState("");
  const [severity, setSeverity] = useState(0.8);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    api.get("/geo/summary").then(({ data }) => setSummary(data)).catch(() => toast.error("Could not load geo data"));
    api.get("/geo/value-chains").then(({ data }) => setNames(data.country_names)).catch(() => {});
    api.get("/geo/chokepoints").then(({ data }) => setChokepoints(data.items)).catch(() => {});
    api.get("/geo/materials").then(({ data }) => setMaterials(data.items)).catch(() => {});
    api.get("/geo/scenario-options").then(({ data }) => { setOptions(data); setTarget(data.chokepoint?.[0] || ""); }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const targets = useMemo(() => (options ? options[shock] || [] : []), [options, shock]);

  const changeShock = (next) => {
    setShock(next);
    setResult(null);
    setTarget((options?.[next] || [])[0] || "");
  };

  const runScenario = async () => {
    try {
      const { data } = await api.post("/geo/simulate", { shock_type: shock, target, severity });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Simulation failed"); }
  };

  const cn = (code) => names[code] || code;

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Geopolitical exposure" description="Cross-border trade-flow & geopolitical risk." path="/geopolitics" />
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-10" data-testid="geopolitics-page">
        <h1 className="ek-eyebrow">Trade-flow risk</h1>
        <h2 className="mt-1 font-display text-3xl font-bold text-ek-ink">Geopolitical &amp; trade-flow exposure</h2>
        <p className="mt-2 max-w-2xl text-sm text-ek-ink/65">
          Risk that lives in the cross-border <em>flow</em> of value — dyadic tension, chokepoints, critical-material
          concentration and labour/visa mobility — scored deterministically across your value chains.
        </p>

        <IntroNote icon={Globe2} title="What you're looking at" testid="geo-intro" className="mt-6">
          A money-weighted view of geopolitical risk in your supply and value chains. The Geo Index blends
          nation-pair tension, maritime chokepoint stress, critical-material concentration and labour mobility;
          the simulator lets you stress-test a shock (e.g. a strait closure) and see the £ impact instantly.
        </IntroNote>

        {/* Headline */}
        {summary && (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="geo-headline">
            <div className="rounded-md border border-black/5 bg-white p-5">
              <p className="ek-eyebrow flex items-center gap-1"><Globe2 size={12} /> Geo exposure index</p>
              <p className={`mt-1 font-display text-5xl font-bold ${bandColor(summary.band)}`} data-testid="geo-index">{summary.geo_index}</p>
              <p className="mt-1 text-xs uppercase text-ek-ink/65">{summary.band} band</p>
            </div>
            <div className="rounded-md border border-black/5 bg-white p-5">
              <p className="ek-eyebrow">Value at risk</p>
              <p className="mt-1 font-display text-3xl font-bold text-ek-ink">{gbp(summary.value_at_risk_gbp)}</p>
              <p className="mt-1 text-xs text-ek-ink/65">{summary.chains?.length} cross-border value chains</p>
            </div>
            <div className="rounded-md border border-black/5 bg-white p-5">
              <p className="ek-eyebrow">Top tension dyads</p>
              <div className="mt-2 space-y-1" data-testid="geo-top-dyads">
                {summary.top_dyads?.slice(0, 4).map((d) => (
                  <div key={d.pair} className="flex items-center justify-between text-xs">
                    <span className="text-ek-ink/70">{cn(d.a)} <ArrowRight size={10} className="inline" /> {cn(d.b)}</span>
                    <span className={`font-semibold ${d.score >= 0.6 ? "text-red-600" : "text-amber-600"}`}>{Math.round(d.score * 100)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Live trade dependency (UN Comtrade) + derived freight pressure */}
        {summary?.trade?.live && (
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="geo-trade-panel">
            <div className="rounded-md border border-black/5 bg-white p-5 lg:col-span-2">
              <p className="ek-eyebrow flex items-center gap-1">
                <TrendingUp size={12} /> Live trade dependency · UK imports {summary.trade.year}
                <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">UN Comtrade</span>
              </p>
              <div className="mt-3 space-y-1.5" data-testid="geo-trade-deps">
                {summary.trade.top_dependencies?.map((t) => (
                  <div key={t.country} className="flex items-center gap-2 text-xs">
                    <span className="w-28 shrink-0 text-ek-ink/70">{t.name}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-black/5">
                      <div className="h-full rounded-full bg-ek-purple" style={{ width: `${t.intensity * 100}%` }} />
                    </div>
                    <span className="w-16 shrink-0 text-right font-semibold text-ek-ink">{gbpShort(t.value_gbp)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-md border border-black/5 bg-white p-5">
              <p className="ek-eyebrow flex items-center gap-1"><Ship size={12} /> Freight-cost pressure</p>
              <p className={`mt-1 font-display text-5xl font-bold ${bandColor(summary.freight_band)}`} data-testid="geo-freight-index">{summary.freight_index}</p>
              <p className="mt-1 text-xs text-ek-ink/65">
                Derived from chokepoint stress × live trade volume on each route.
              </p>
            </div>
          </div>
        )}

        {/* Value chains */}
        {summary?.chains?.length > 0 && (
          <div className="mt-10">
            <GeoArcMap chains={summary.chains} />
          </div>
        )}
        <h3 className="mt-10 ek-eyebrow">Value chains</h3>
        <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="geo-chains">
          {summary?.chains?.map((c) => (
            <div key={c.chain_id} className="rounded-md border border-black/5 bg-white p-5" data-testid={`chain-${c.chain_id}`}>
              <div className="flex items-start justify-between">
                <h4 className="font-display text-sm font-semibold text-ek-ink">{c.name}</h4>
                <span className={`font-display text-2xl font-bold ${idxColor(c.composite).replace("bg-", "text-")}`}>{c.composite}</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1 text-xs text-ek-ink/60">
                {c.country_path.map((code, i) => (
                  <span key={i} className="flex items-center gap-1">
                    <span className="rounded bg-ek-canvas px-1.5 py-0.5 font-medium">{code}</span>
                    {i < c.country_path.length - 1 && <ArrowRight size={10} className="text-ek-ink/30" />}
                  </span>
                ))}
              </div>
              <div className="mt-3 space-y-1.5">
                <Bar label="Path tension" value={c.path_tension} />
                <Bar label="Chokepoint" value={c.chokepoint_stress} />
                <Bar label="Material" value={c.material_risk} />
                <Bar label="Labour / visa" value={c.labour_risk} />
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {c.chokepoints.map((k) => <span key={k} className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] text-blue-700">{k}</span>)}
                {c.materials.map((k) => <span key={k} className="rounded-full bg-purple-50 px-2 py-0.5 text-[10px] text-purple-700">{k}</span>)}
                {c.labour_country && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] text-amber-700">labour: {c.labour_country}</span>}
              </div>
              <p className="mt-3 text-xs text-ek-ink/65">VaR {gbp(c.value_at_risk_gbp)}</p>
              {c.trade_value_gbp > 0 && (
                <div className="mt-2 flex items-center justify-between border-t border-black/5 pt-2 text-[11px]" data-testid={`chain-trade-${c.chain_id}`}>
                  <span className="text-ek-ink/65">Trade dependency <strong className="text-ek-ink/70">{gbpShort(c.trade_value_gbp)}</strong></span>
                  <span className="text-ek-ink/65">Freight <strong className={c.freight_pressure >= 0.6 ? "text-red-600" : c.freight_pressure >= 0.35 ? "text-amber-600" : "text-emerald-600"}>{Math.round(c.freight_pressure * 100)}%</strong></span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Scenario simulator */}
        <h3 className="mt-10 ek-eyebrow flex items-center gap-1"><Zap size={12} /> Geo scenario simulator</h3>
        <div className="mt-3 rounded-md border border-black/5 bg-white p-5" data-testid="geo-simulator">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-ek-ink/65">Shock type
              <select value={shock} onChange={(e) => changeShock(e.target.value)} data-testid="geo-shock-select" className="mt-1 block rounded-md border border-black/10 px-3 py-2 text-sm">
                {SHOCKS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
            <label className="text-xs text-ek-ink/65">Target
              <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="geo-target-select" className="mt-1 block rounded-md border border-black/10 px-3 py-2 text-sm">
                {targets.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="text-xs text-ek-ink/65">Severity: <b>{severity}</b>
              <input type="range" min="0.1" max="1" step="0.1" value={severity} onChange={(e) => setSeverity(parseFloat(e.target.value))} data-testid="geo-severity-slider" className="mt-1 block w-40" />
            </label>
            <button onClick={runScenario} className="ek-btn flex items-center gap-1.5" data-testid="geo-simulate-button"><ShieldAlert size={15} /> Simulate</button>
          </div>

          {result && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-black/5 pt-4 sm:grid-cols-3" data-testid="geo-result">
              <div>
                <p className="ek-eyebrow">Headline impact</p>
                <p className="mt-1 font-display text-3xl font-bold text-red-600" data-testid="geo-result-headline">{gbp(result.headline_gbp)}</p>
                <p className="mt-1 text-xs text-ek-ink/65">{result.impacted_count} chain(s) impacted</p>
              </div>
              <div>
                <p className="ek-eyebrow">Geo index shift</p>
                <p className="mt-1 font-display text-3xl font-bold text-ek-ink">
                  {result.geo_index_baseline} <ArrowRight size={18} className="inline text-ek-ink/30" /> <span className={bandColor(result.geo_index_scenario >= 60 ? "red" : result.geo_index_scenario >= 35 ? "amber" : "green")}>{result.geo_index_scenario}</span>
                </p>
                <p className="mt-1 text-xs font-semibold text-red-600">+{result.delta} points</p>
              </div>
              <div>
                <p className="ek-eyebrow">Impacted chains</p>
                <div className="mt-1 space-y-1">
                  {result.impacted.map((c) => (
                    <p key={c.chain_id} className="text-xs text-ek-ink/60">{c.name} <span className="text-red-500">{c.composite_before}→{c.composite_after}</span></p>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Registries */}
        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-md border border-black/5 bg-white p-5" data-testid="geo-chokepoints">
            <h3 className="ek-eyebrow flex items-center gap-1"><Ship size={12} /> Logistics chokepoints</h3>
            <div className="mt-3 space-y-2">
              {chokepoints.map((c) => (
                <div key={c.key} className="flex items-center gap-3">
                  <span className="w-36 truncate text-sm text-ek-ink">{c.name}</span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/5">
                    <div className={`h-full rounded-full ${idxColor(c.stress * 100)}`} style={{ width: `${c.stress * 100}%` }} />
                  </div>
                  <span className="w-8 text-right text-xs font-semibold text-ek-ink/60">{Math.round(c.stress * 100)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-md border border-black/5 bg-white p-5" data-testid="geo-materials">
            <h3 className="ek-eyebrow flex items-center gap-1"><Boxes size={12} /> Critical-material concentration</h3>
            <div className="mt-3 space-y-2">
              {materials.slice(0, 6).map((m) => (
                <div key={m.key} className="flex items-center gap-3">
                  <span className="w-36 truncate text-sm text-ek-ink">{m.name}</span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/5">
                    <div className={`h-full rounded-full ${idxColor(m.concentration * 100)}`} style={{ width: `${m.concentration * 100}%` }} />
                  </div>
                  <span className="w-16 text-right text-[11px] text-ek-ink/65">{cn(m.top_producer)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
